/**
 * Spotlight coachmark tour — replaces the previous purely-modal firstRunTour.
 *
 * For each step the tour:
 *   1. Finds a real DOM element by CSS selector.
 *   2. Draws a dim overlay around it (4 absolutely-positioned divs that
 *      together form a "donut" with the target as the hole, plus a thin
 *      coloured ring around the cutout).
 *   3. Anchors a tooltip card next to it with title, body, "Skip / Next".
 *   4. Advances when the user clicks Next OR clicks the highlighted target.
 *
 * Two playlists are exported (heistFirstPlaylist / mcpFirstPlaylist) — the
 * setup orchestrator picks one based on the wizard's Step-3 fork.
 *
 * Re-entry from `#?tour=1` runs the user's last-picked playlist (default heist).
 * Server flag `tour_completed` is set on completion or skip via client.completeTour().
 */

import { client } from '../api/client.js';
import { arrowRight } from './icons.js';

const STORAGE_PLAYLIST = 'heist.tour.playlist';      // 'heist' | 'mcp'
const STORAGE_STEP = 'heist.tour.step';              // numeric index, persists refresh

// ── Tour body sanitiser ───────────────────────────────────────────────
// Step body strings can contain a small set of inline HTML tags
// (<strong>, <em>, <code>, <br>, <a href="…">). The renderer pipes them
// through this allowlist before assigning to innerHTML. All bodies in
// this file are source constants so this is belt-and-suspenders; the
// sanitiser exists so we can confidently add new HTML tags to existing
// step copy without re-auditing the renderer each time.
const TOUR_TAG_ALLOWLIST = /^(strong|em|b|i|code|br)$/i;
function sanitizeTourBody(html) {
    if (html == null) return '';
    let s = String(html);
    // Strip every tag that isn't on the allowlist. Anchor tags get
    // special handling so href + target survive but onclick / javascript:
    // are scrubbed.
    s = s.replace(/<a\s+([^>]*)>/gi, (_m, attrs) => {
        const hrefMatch = attrs.match(/href\s*=\s*(['"])([^'"]+)\1/i);
        const href = hrefMatch ? hrefMatch[2] : '';
        if (!href || /^\s*javascript:/i.test(href)) return '';
        const safeHref = href.replace(/"/g, '&quot;');
        return `<a href="${safeHref}" target="_blank" rel="noopener">`;
    });
    s = s.replace(/<\/?([a-zA-Z0-9]+)(\s[^>]*)?>/g, (m, tag) => {
        if (tag.toLowerCase() === 'a') return m.toLowerCase().startsWith('</') ? '</a>' : m;
        return TOUR_TAG_ALLOWLIST.test(tag) ? `<${m.startsWith('</') ? '/' : ''}${tag.toLowerCase()}>` : '';
    });
    return s;
}
const RING_PADDING = 6;                              // px around the cutout

/* Two ordered playlists. Each step has:
 *   - selector: CSS selector for the highlighted element.
 *   - title:    short headline shown in the tooltip.
 *   - body:     1-sentence explainer.
 *   - placement?: 'right' | 'left' | 'top' | 'bottom' — tooltip side; auto if omitted.
 *   - waitForSelector?: don't render this step until this selector exists in the DOM
 *                       (used when a stop targets an element on a page the user
 *                       hasn't navigated to yet).
 */
export const heistFirstPlaylist = [
    {
        selector: '[data-testid="nav-brain"]',
        title: 'Your brand brain lives here',
        body: 'Click The Brain whenever you want to update your voice, mission, or anything the AI should remember.',
    },
    {
        selector: '[data-testid="nav-create"]',
        title: 'Generate posts in Content Studio',
        body: 'One click and the Brain writes a draft in your voice. Open Content Studio to give it a try.',
    },
    {
        selector: '[data-testid="nav-library"]',
        title: 'Every draft lands in Library',
        body: 'Edit, score, schedule, or delete from one place — drafts the AI writes for you all show up here.',
    },
    {
        selector: '[data-testid="nav-calendar"]',
        title: 'Schedule on the Calendar',
        body: 'Drag a draft onto a date. Heist auto-publishes to your connected platforms.',
    },
    {
        // Calendar reminders — shown only when the user is on /#calendar.
        // `optional: true` is what makes this skip immediately when the
        // target isn't on the page; waitForSelector alone does NOT skip —
        // it hides the tooltip and blocks for the full 8s timeout, which
        // froze the tour for every user who pressed "Next" from /#home.
        selector: '[data-testid="calendar-add-reminder"]',
        title: 'Block out time for the work behind the post',
        body: 'Use + Reminder to put script-writing, shoot days, or planning sessions on the same calendar. Heist will ping you when it\'s time.',
        optional: true,
    },
    {
        // Final step pivots to MCP rather than vaguely promising "a separate
        // tour later." There's no top-level Connectors nav entry, so we
        // spotlight Settings (where the Connectors row lives). Clicking
        // Settings would normally advance the tour AND navigate, which would
        // strand the user on Settings while the tour pivots to /#connectors.
        // noClickAdvance forces the user to use the "Show me MCP →" button
        // on the tooltip — which advances cleanly, fires the pivot, and
        // jumps them straight to /#connectors with the MCP walkthrough.
        selector: '[data-testid="nav-settings"]',
        title: 'Want Claude or ChatGPT to write for you?',
        body: 'Connect your AI chat in seconds — the fastest way to use Heist. Click "Show me MCP" below and we\'ll walk you through the URL paste.',
        noClickAdvance: true,
        pivotTo: 'mcp',
    },
];

/**
 * Settings tour — runs when the user clicks "Take a tour of Settings" on
 * the Settings page header. One step per visible nav item so the user
 * learns the entire shape of the page. Pro/workspace-gated rows are
 * flagged `optional`: showStep skips them immediately when the target
 * isn't in the DOM (no 8-second waitForRect freeze for free-tier users).
 *
 * `noClickAdvance` is set on the two nav-away rows (Connectors, Agent
 * Inbox) so clicking them navigates without the tour also advancing —
 * otherwise the next step's selector would resolve against the new page
 * and the tour would chain into a long wait-and-skip dance.
 */
export const settingsPlaylist = [
    {
        selector: '[data-testid="settings-help-tour-trigger"]',
        title: 'Settings, in four groups',
        body: 'Connections is for hooking Heist up to other tools. Content controls how Heist writes and schedules. Account holds profile + billing + team. Privacy lets you export or delete your data.',
    },
    {
        selector: '.settings-nav-item[data-section="section-integrations"]',
        title: 'Integrations',
        body: 'Wire Heist to Facebook, Instagram, LinkedIn, TikTok, YouTube, WordPress, and your website. Each one is a separate OAuth handshake; tokens stay encrypted at rest.',
        settingsSectionId: 'section-integrations',
    },
    {
        selector: '.settings-nav-item[data-section="section-ai-settings"]',
        title: 'AI Settings',
        body: 'Pick whether Heist generates with our shared keys ("My Plan") or your own ("Bring Your Own Key"). BYOK gives you cost control and bypasses our monthly token caps.',
        settingsSectionId: 'section-ai-settings',
    },
    {
        selector: '.settings-nav-item[data-section="section-agent-inbox"]',
        title: 'Agent Inbox',
        body: 'Pro+ feature. Drafts written by your AI chat queue here for review and approval before they hit the calendar. The setup walkthrough + a copyable starter prompt live in the Help center — link is right inside this tab.',
        optional: true,
        settingsSectionId: 'section-agent-inbox',
    },
    {
        selector: '.settings-nav-item[data-section="schedule-rules-section"]',
        title: 'Schedule',
        body: 'Posting cadence rules like "Post to Instagram Tue/Thu at 9am, never weekends." Heist auto-fill uses these to slot drafts onto the calendar.',
        settingsSectionId: 'schedule-rules-section',
    },
    {
        selector: '.settings-nav-item[data-section="section-brands"]',
        title: 'Brands',
        body: 'Each brand has its own Brain, library, calendar, and integrations. Switch between them here, or add a new one if you manage multiple businesses.',
        settingsSectionId: 'section-brands',
    },
    {
        selector: '.settings-nav-item[data-section="section-account"]',
        title: 'Profile',
        body: 'Your name, photo, email, password, and timezone. Personal — these don\'t change anything brand-related.',
        settingsSectionId: 'section-account',
    },
    {
        selector: '.settings-nav-item[data-section="section-billing"]',
        title: 'Billing',
        body: 'Plan, payment method, invoice history, seat count. Upgrades take effect immediately; downgrades apply at the next renewal.',
        settingsSectionId: 'section-billing',
    },
    {
        selector: '.settings-nav-item[data-section="section-team-management"]',
        title: 'Team',
        body: 'Invite teammates and assign roles (Owner, Admin, Editor, Viewer). Available on Pro and above.',
        settingsSectionId: 'section-team-management',
    },
    {
        selector: '.settings-nav-item[data-section="section-approvals"]',
        title: 'Approvals',
        body: 'Workspace Owners and Admins see this. Review and approve drafts your team submitted before they hit the calendar.',
        optional: true,
        settingsSectionId: 'section-approvals',
    },
    {
        selector: '.settings-nav-item[data-section="section-activity"]',
        title: 'Activity',
        body: 'Workspace Owners and Admins see this. Audit timeline of who did what — created, edited, scheduled, published, deleted.',
        optional: true,
        settingsSectionId: 'section-activity',
    },
    {
        selector: '.settings-nav-item[data-section="section-team"]',
        title: 'Data & Privacy',
        body: 'Export your data as JSON, or delete your account. Heist never trains models on your content.',
        settingsSectionId: 'section-team',
    },
];

export const mcpFirstPlaylist = [
    // Hero step removed — the deep-link scrolls the primary card into view,
    // which leaves .conn-hero offscreen above. The spotlight ring would
    // anchor off-page and the masks would freeze the screen with nothing
    // visible to interact with. Start at the first actionable card.
    {
        selector: '.conn-card--primary [data-action="copy-text"][data-copy="Heist"]',
        title: 'Step 1 — copy the name',
        body: 'In your AI chat (Claude.ai, ChatGPT, …), open its settings and find Connectors → Add custom connector. Paste this in the Name field.',
        waitForSelector: '.conn-card--primary',
    },
    {
        selector: '.conn-card--primary [data-copy^="http"]',
        title: 'Step 2 — copy the URL',
        body: 'Paste this into the URL field of the same dialog. That\'s all your AI chat needs to reach Heist.',
    },
    {
        // Step 4 on the page = Step 3 in the tour. Spotlight the
        // optional starter-prompt field so the user knows there's a
        // copy-pasteable kickstart for filling in their brand profile.
        selector: '.conn-fake-field--prompt',
        title: 'Step 3 — paste this prompt to set up your brand (optional)',
        body: 'After Heist is connected, copy the prompt below and paste it in your AI chat. The AI walks you through filling in your brand profile — voice, audience, goals — and saves it to Heist as you go. Skip this if you\'d rather fill the Brain in manually.',
        waitForSelector: '.conn-fake-field--prompt',
    },
    {
        selector: '[data-testid="nav-brain"]',
        title: 'View or edit your Brain anytime',
        body: 'If you set up your Brain via your AI chat, everything it saved (mission, voice, audience, memory facts, documents) lives here. You can also fill it in manually or tweak any field — both paths land in the same place.',
    },
    {
        selector: '[data-testid="nav-home"]',
        title: 'Track everything from the Command Center',
        body: 'Drafts, schedules, and connected agents all show up here. You\'re ready to roll.',
    },
];

let activeOverlay = null;
let activeStep = -1;
let activePlaylist = null;
let activeName = null;
let resizeRaf = 0;
let onKeyDown = null;
let onResize = null;
let onTargetClick = null;
let observedTarget = null;
let pendingWaitObserver = null;

function persistStep(idx) {
    try { localStorage.setItem(STORAGE_STEP, String(idx)); } catch (_) {}
}

function persistPlaylist(name) {
    try { localStorage.setItem(STORAGE_PLAYLIST, name); } catch (_) {}
}

function readPlaylistName() {
    try { return localStorage.getItem(STORAGE_PLAYLIST) || 'heist'; } catch (_) { return 'heist'; }
}

function readStep() {
    try {
        const n = parseInt(localStorage.getItem(STORAGE_STEP) || '0', 10);
        return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch (_) { return 0; }
}

function clearStorage() {
    try { localStorage.removeItem(STORAGE_STEP); } catch (_) {}
}

function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/**
 * Page-scoped playlists fired by the per-page "?" button in pageHeader.js.
 *
 * Design rules:
 *   - 3 steps each (4 max). Anything longer feels like a course, not a tour.
 *   - Spotlight stable selectors only — `data-testid` first, then class
 *     names that are unlikely to change. Avoid IDs that get regenerated.
 *   - `optional: true` on any element that doesn't always render (e.g.
 *     a panel that only appears when a brand has data) so the tour skips
 *     instead of waiting 8s on a missing target.
 *   - Body copy ≤ 1 sentence. Tell the user what the spotlighted thing
 *     does, not how it was implemented.
 */
export const homePlaylist = [
    {
        selector: '[data-testid="dashboard-upcoming"]',
        title: 'Your upcoming queue',
        body: 'Posts scheduled for the next few days. Click any row to edit, reschedule, or publish on the spot.',
    },
    {
        selector: '[data-testid="dashboard-checklist"]',
        title: 'Brain Intelligence',
        body: 'Setup completeness across your AI key, brand profile, MCP, and posting history. Every item links to where to fix it.',
    },
    {
        selector: '[data-testid="dashboard-quick-actions"]',
        title: 'Jump anywhere',
        body: 'Quick links to Studio, Campaigns, Calendar, and the Brain — the places you visit most.',
    },
];

/**
 * Brain tour — walks all three tabs (Brand Strategy / Knowledge Base /
 * Chat). Each tab has dedicated steps; `clickFirst` swaps the tab
 * before measuring rects.
 */
export const brainPlaylist = [
    // ── Overview ────────────────────────────────────────────────────────
    {
        selector: '[data-testid="brain-tabs"], .brain-tabs',
        title: 'Three views of your Brain',
        body: 'Brand Strategy is your voice + audience + offers. Knowledge Base holds documents and uploaded brand material. Chat is where you talk to the agent and shape the Brain in plain English.',
    },

    // ── Brand Strategy tab ──────────────────────────────────────────────
    {
        selector: '[data-testid="brain-view-brand"], [data-testid="brain-memory-panel"]',
        title: 'Brand Strategy',
        body: 'Mission, audience, voice, offers, CTA, do-not-say words. Every draft Heist generates is grounded in these fields.',
        clickFirst: '[data-testid="brain-tab-brand"]',
        optional: true,
    },
    {
        selector: '[data-testid="audience-personas-card"], .brain-personas-card',
        title: 'Personas',
        body: 'Define one or many target personas — Heist tailors voice and angle per persona when you ask the AI to write for a specific one.',
        clickFirst: '[data-testid="brain-tab-brand"]',
        optional: true,
    },
    {
        selector: '[data-testid="add-fact-btn"]',
        title: 'Memory facts',
        body: 'Persistent things the AI should remember — product names, pricing, awards, do-not-say rules, audience signals. Stored once, used in every future generation.',
        clickFirst: '[data-testid="brain-tab-brand"]',
        optional: true,
    },

    // ── Knowledge Base tab ──────────────────────────────────────────────
    {
        selector: '[data-testid="brain-vault-card"], [data-testid="brain-view-documents"]',
        title: 'Knowledge Base',
        body: 'Upload PDFs, DOCX, TXT, CSV — Heist extracts the text and the AI reads it during generation. Great for brand guides, product specs, past blog posts.',
        clickFirst: '[data-testid="brain-tab-documents"]',
        optional: true,
    },
    {
        selector: '[data-testid="brain-vault-upload"], #vault-upload-zone',
        title: 'Drop a file in',
        body: 'Drag any document here or click to browse. The AI ingests the text within seconds and grounds future drafts in it.',
        clickFirst: '[data-testid="brain-tab-documents"]',
        optional: true,
    },
    {
        selector: '[data-testid="brain-vault-list"], .vault-table',
        title: 'Document library',
        body: 'Everything you\'ve uploaded, with filenames, sizes, and ingest status. Remove a file and the AI stops referencing it on the next generation.',
        clickFirst: '[data-testid="brain-tab-documents"]',
        optional: true,
    },

    // ── Chat with Brain tab ─────────────────────────────────────────────
    {
        selector: '[data-testid="brain-chat-workspace"], [data-testid="brain-view-chat"]',
        title: 'Chat with your Brain',
        body: 'A full conversational assistant that has all your brand context loaded. Ask questions, draft posts, refine the Brain itself — all without leaving the page.',
        clickFirst: '[data-testid="brain-tab-chat"]',
        optional: true,
    },
    {
        selector: '[data-testid="brain-chat-new"]',
        title: 'New thread',
        body: 'Each thread keeps its own context. Use one for daily drafting, another for brand-strategy planning, a third for content audits.',
        clickFirst: '[data-testid="brain-tab-chat"]',
        optional: true,
    },
    {
        selector: '[data-testid="brain-chat-input"]',
        title: 'Talk in plain English',
        body: 'Ask "what\'s my best-performing post?" or "rewrite this in a more casual voice." The agent uses the same MCP tools your external AI chat uses.',
        clickFirst: '[data-testid="brain-tab-chat"]',
        optional: true,
    },
];

/**
 * Content Studio tour — walks the simple-first generate bar, the
 * Social/Blog workbench toggle, and (when expanded) the three social
 * modes. Selectors verified against the live create page (Phase 4
 * audit) — the previous playlist had several speculative IDs that
 * don't exist, which made the tour stall waiting for them.
 */
// ── Phase 42 — Content Studio tour, split per tab ─────────────────────
// The previous monolithic createPlaylist (12 mixed Social/Blog/Video
// steps) froze users when clickFirst switched tabs but the new tab's
// content hadn't rendered yet. Three small focused playlists + the
// dispatcher inside startCoachmarkTour() pick the right one based on
// localStorage.activeCreateMode. Every step that depends on a tab
// switch or conditionally-rendered element now carries both
// `optional: true` (skip on miss instead of polling) AND
// `waitForSelector` (wait for the right element to actually appear
// before measuring its rect).

/**
 * Shared opener step — same intro across all three sub-playlists so
 * the user gets oriented regardless of which tab they entered from.
 */
const CREATE_MODE_TOGGLE_STEP = {
    selector: '[data-testid="create-mode-toggle"]',
    title: 'Social, Blog, or Video?',
    body: 'Three workbenches in Content Studio: <strong>Social</strong> (multi-platform short-form), <strong>Blog</strong> (long-form, WordPress publish), and <strong>Faceless Video</strong> (script → voiceover → MP4). This tour focuses on the tab you\'re on — switch tabs anytime and click the <strong>?</strong> button again for a focused tour of that tab.',
    optional: true,
};

/**
 * Social Studio tour — fires when activeCreateMode === 'social'.
 * No tab-switching mid-tour. Every step targets an element that's
 * visible on the Social tab.
 */
export const createSocialPlaylist = [
    CREATE_MODE_TOGGLE_STEP,
    {
        selector: '[data-testid="topic-input"]',
        title: 'Topic in, draft out',
        body: 'Type a topic line. Heist pulls voice + audience + offers from your Brand Brain and generates a draft per selected platform.',
        optional: true,
        waitForSelector: '[data-testid="topic-input"]',
    },
    {
        selector: '.create-platform-pills',
        title: 'Pick the platforms',
        body: 'Each platform gets a tailored draft — character limits, hashtag conventions, image specs enforced automatically. Tap any pill to add or remove.',
        optional: true,
        waitForSelector: '.create-platform-pills',
    },
    {
        selector: '[data-testid="generate-btn"]',
        title: 'Generate',
        body: 'One click drafts a post per selected platform. Drafts land in the editor below with a brand-alignment score. Edit any one inline before saving.',
        optional: true,
        waitForSelector: '[data-testid="generate-btn"]',
    },
    {
        selector: '#gen-ai-image-check',
        title: '🖼 AI images for every post',
        body: 'Check this and every social draft in the batch gets its own AI-generated image, sized for the target platform. <strong>Also works with images from your AI chat</strong> — generate one in Claude.ai or ChatGPT, then say "send that image to my latest Heist draft" and it lands here automatically.',
        optional: true,
        waitForSelector: '#gen-ai-image-check',
    },
    {
        selector: '#gen-faceless-video-check',
        title: '🎬 Faceless video for video-eligible platforms',
        body: 'Check this when generating for TikTok / Reels / Shorts / YouTube — Heist also produces a script-driven faceless video for each video-eligible draft in the batch. Render runs in the background; videos land in Library when they finish.',
        optional: true,
        waitForSelector: '#gen-faceless-video-check',
    },
    {
        selector: '[data-testid="social-mode-segments"]',
        title: 'Three social modes',
        body: 'AI Complete writes the whole post from a topic. AI Assisted gives you outlines, hooks, and rewrites on demand. Manual lets you write or paste your own and just use Heist for scheduling.',
        optional: true,
        waitForSelector: '[data-testid="social-mode-segments"]',
    },
    {
        selector: '.create-editor',
        title: 'Edit + score inline',
        body: 'The editor below the generate bar holds your active draft. Brand-alignment score updates as you edit. Save the draft to your library, or schedule it from the actions row.',
        optional: true,
        waitForSelector: '.create-editor',
    },
    {
        selector: '#gmw-create-launch',
        title: 'Plan a whole month',
        body: 'Skip the one-post-at-a-time loop — kick off the campaign wizard to generate 14–30 scheduled posts from a single topic.',
        optional: true,
        waitForSelector: '#gmw-create-launch',
    },
];

/**
 * Blog editor tour — fires when activeCreateMode === 'blog'.
 * Targets selectors that exist on the Blog workbench only.
 */
export const createBlogPlaylist = [
    CREATE_MODE_TOGGLE_STEP,
    {
        selector: '[data-testid="blog-editor-root"]',
        title: 'Long-form editor',
        body: 'Rich-text editor with heading styles, embedded images, tables, format painter, brand-alignment scoring, and one-click WordPress publish.',
        optional: true,
        waitForSelector: '[data-testid="blog-editor-root"]',
    },
    {
        selector: '[data-testid="blog-title-input"]',
        title: 'Post title',
        body: 'Start with a working title — it drives the URL slug, the AI image prompt (if you use the Generate-with-AI button below), and the social-share preview. You can change it later.',
        optional: true,
        waitForSelector: '[data-testid="blog-title-input"]',
    },
    {
        selector: '[data-testid="blog-topic-input"]',
        title: 'Topic + context',
        body: 'Give the AI a sentence about what this post should cover. The richer the topic, the more on-brand the draft. Add extra instructions in the Context field below (audience, tone tweaks, must-include points).',
        optional: true,
        waitForSelector: '[data-testid="blog-topic-input"]',
    },
    {
        selector: '[data-testid="blog-featured-ai"]',
        title: '🖼 AI featured image',
        body: 'Generate a 16:9 editorial featured image from your title + first paragraph in ~10 seconds. <strong>Tip:</strong> from your AI chat you can also say "Heist, generate a cinematic featured image for my latest blog post and attach it."',
        optional: true,
        waitForSelector: '[data-testid="blog-featured-ai"]',
    },
    {
        selector: '[data-testid="blog-generate-btn"]',
        title: 'Generate draft',
        body: 'Heist writes the full post from your title + topic + context, applying your brand voice. Edit any section inline after the draft lands — the AI assist toolbar above the editor offers rewrites, expansions, and tone shifts on demand.',
        optional: true,
        waitForSelector: '[data-testid="blog-generate-btn"]',
    },
    {
        selector: '[data-testid="blog-drafts-toggle"]',
        title: 'Blog drafts drawer',
        body: 'All your blog drafts live here — saved automatically as you write, never lost. Click to flip between posts without leaving the editor.',
        optional: true,
        waitForSelector: '[data-testid="blog-drafts-toggle"]',
    },
];

/**
 * Faceless Video tour — fires when activeCreateMode === 'video'.
 * Targets selectors that exist on the Video tab only.
 */
export const createVideoPlaylist = [
    CREATE_MODE_TOGGLE_STEP,
    {
        selector: '[data-testid="video-editor-root"]',
        title: '🎬 Faceless Video',
        body: 'Script → AI voiceover → slideshow MP4 with burned-in captions. Ready for TikTok / Reels / Shorts. Renders in 30–90 seconds. <strong>Tip:</strong> you can do this from your AI chat too — just say "make me a 60-second video about X."',
        optional: true,
        waitForSelector: '[data-testid="video-editor-root"]',
    },
    {
        selector: '[data-testid="video-script-input"]',
        title: 'Script in, video out',
        body: 'Paste your script (60-second target ≈ 150 words), or click <strong>Write with AI</strong> at the top of the card to draft one from a topic. The voice reads it word-for-word.',
        optional: true,
        waitForSelector: '[data-testid="video-script-input"]',
    },
    {
        selector: '[data-testid="video-voice-select"]',
        title: 'Pick a voice',
        body: 'Loaded from your ElevenLabs account. Click <strong>Set as brand default</strong> once and Heist remembers it next time you make a video for the same brand.',
        optional: true,
        waitForSelector: '[data-testid="video-voice-select"]',
    },
    {
        selector: '[data-testid="video-visual-style-select"]',
        title: 'Pick a visual style',
        body: '<strong>Slideshow</strong> uses stock photos + voiceover — fast and cheap (~$0.12 per video). <strong>AI video</strong> uses Runway to generate short cinematic clips that get stitched into the slideshow — premium look, ~10–15× the cost.',
        optional: true,
        waitForSelector: '[data-testid="video-visual-style-select"]',
    },
    {
        selector: '[data-testid="video-cost-meter-summary"]',
        title: 'Cost is always upfront',
        body: 'Click here anytime to see how much your next render will cost. Heist never charges you anything — you bring your own ElevenLabs and Creatomate (and optionally Runway) accounts in <strong>Settings → AI</strong>.',
        optional: true,
        waitForSelector: '[data-testid="video-cost-meter-summary"]',
    },
    {
        selector: '[data-testid="video-generate-btn"]',
        title: 'Generate Video',
        body: 'Click and Heist runs the pipeline: voiceover → visuals → composition. Stage labels show progress (Writing voiceover → Finding visuals → Composing slideshow). Your MP4 lands in Library when done.',
        optional: true,
        waitForSelector: '[data-testid="video-generate-btn"]',
    },
];

/**
 * Deprecated — kept as an alias of createSocialPlaylist for one
 * release so any external imports of `createPlaylist` keep working.
 * The dispatcher inside startCoachmarkTour() routes `'create'` to
 * the right sub-playlist based on activeCreateMode.
 */
export const createPlaylist = createSocialPlaylist;

/**
 * Library tour — walks toolbar filters, the grid, the editor drawer
 * pattern, and the bulk-action footer. Library is filter-driven (not
 * tabbed), so steps focus on the toolbar + grid mechanics.
 */
export const libraryPlaylist = [
    {
        selector: '.library-toolbar',
        title: 'Filter + search',
        body: 'Find any draft fast — search text, narrow by status, platform, brand score, or date. Filters stack and persist as you scroll.',
    },
    {
        selector: '#library-search',
        title: 'Search across content',
        body: 'Type any keyword from a draft\'s body, title, or platform handle. Matches highlight in the grid below as you type.',
        optional: true,
    },
    {
        selector: '[data-testid="filter-date"]',
        title: 'Date range',
        body: 'Filter by created or scheduled date — find "everything I drafted last week" or "what\'s scheduled for next month."',
        optional: true,
    },
    {
        selector: '[data-testid="library-grid"], [data-testid="library-list"]',
        title: 'Every draft lives here',
        body: 'One card per draft, with platform badge, brand score, and quick-status. Click any card to open the full-edit drawer on the right.',
    },
    {
        selector: '[data-testid="library-card"]',
        title: 'Brand-alignment score',
        body: 'The number on each card is the brand-alignment score (0-100). Green = on-voice; yellow = drift; red = needs your Brain to be more specific.',
        optional: true,
    },
    {
        selector: '[data-testid="ll-bulk-bar"], .library-bulk-actions',
        title: 'Bulk actions',
        body: 'Select multiple cards and the bulk bar appears — schedule, delete, regenerate, or export many drafts at once.',
        optional: true,
    },
];

/**
 * Campaigns tour — walks the list view, the cadence summary, the
 * detail drawer, and the new-campaign launch button.
 */
export const campaignsPlaylist = [
    {
        selector: '[data-testid="campaigns-create-btn"], #btn-show-create',
        title: 'Plan a campaign',
        body: 'A campaign groups multiple posts under one launch — product launches, holiday calendars, multi-week content series. The wizard generates and schedules them in one shot.',
    },
    {
        selector: '[data-testid="campaigns-stats"]',
        title: 'Campaign rollup',
        body: 'Active count, total posts, average engagement across every campaign you have running.',
        optional: true,
    },
    {
        selector: '[data-testid="campaigns-grid"]',
        title: 'Every campaign in flight',
        body: 'Each row shows status, post count, cadence, and the engagement rollup. Click to drill into the detail view.',
        optional: true,
    },
    {
        selector: '[data-testid="campaign-engagement-line"]',
        title: 'Live engagement per campaign',
        body: 'Heist syncs engagement hourly — see which campaigns are landing and which are flat without leaving this page.',
        optional: true,
    },
    {
        selector: '[data-testid="cadence-summary"], [data-testid="campaign-days"]',
        title: 'Cadence at a glance',
        body: 'Inside any campaign\'s detail view, see exactly which days, platforms, and times the posts are scheduled to land.',
        optional: true,
    },
    {
        selector: '[data-testid="btn-view-calendar"]',
        title: 'Jump to the calendar slice',
        body: 'One click from a campaign to its slice on the main calendar — useful for spotting conflicts with other campaigns running in parallel.',
        optional: true,
    },
];

/**
 * Calendar tour — walks the three view modes (Month / Week / Day) plus
 * the reminders + filter chips. `clickFirst` swaps the FullCalendar
 * view before measuring so each step shows the matching grid.
 */
export const calendarPlaylist = [
    // ── Month view (default) ────────────────────────────────────────────
    {
        selector: '.calendar-view-toggle',
        title: 'Three calendar views',
        body: 'Month for big-picture coverage, Week for cadence at a glance, Day for hour-by-hour scheduling. Heist remembers your last view per session.',
    },
    {
        selector: '.fc, .fc-view-harness',
        title: 'Month view',
        body: 'A bird\'s-eye view of every scheduled post + reminder. Each row across is a week — gaps mean empty days you should fill.',
        clickFirst: '.calendar-view-toggle .segmented-item[data-view="dayGridMonth"]',
        optional: true,
    },

    // ── Week view ───────────────────────────────────────────────────────
    {
        selector: '.fc, .fc-view-harness',
        title: 'Week view',
        body: 'See the next 7 days side-by-side with hour slots. Best for spotting same-time conflicts and tightening cadence.',
        clickFirst: '.calendar-view-toggle .segmented-item[data-view="timeGridWeek"]',
        optional: true,
    },

    // ── Day view ────────────────────────────────────────────────────────
    {
        selector: '.fc, .fc-view-harness',
        title: 'Day view',
        body: 'A single day, hour by hour. Use this when you\'re sequencing multiple posts on a launch day or planning around a meeting block.',
        clickFirst: '.calendar-view-toggle .segmented-item[data-view="timeGridDay"]',
        optional: true,
    },

    // ── Filters + actions ───────────────────────────────────────────────
    {
        selector: '[data-testid="calendar-personal-chip"]',
        title: 'Toggle reminders + posts',
        body: 'Personal reminders are styled differently from posts. Toggle this chip if you want to see only one or the other.',
        clickFirst: '.calendar-view-toggle .segmented-item[data-view="dayGridMonth"]',
        optional: true,
    },
    {
        selector: '[data-testid="calendar-add-reminder"]',
        title: 'Add a reminder',
        body: 'Block out time for the work behind the post — shoot days, script-writing, planning sessions. Reminders ping you at the time you set.',
        optional: true,
    },
    {
        selector: '.fc-event, .fc-daygrid-event',
        title: 'Drag to reschedule',
        body: 'Grab any post or reminder and drop it on a different date or time. Heist re-saves the schedule and re-publishes accordingly.',
        optional: true,
    },
];

/**
 * Analytics tour — walks through all three tabs (Planning / Performance /
 * Team) so users see what the page can actually do, not just the default
 * tab. Steps marked `activateTab: '<tab>'` click the matching
 * [data-analytics-tab="..."] button before the spotlight measures, so
 * the user is reading about the tab they're looking at.
 *
 * Length: ~10 steps. That's longer than the other page tours but matches
 * the page's actual complexity — analytics is the densest surface in the
 * app and a 3-step tour skips most of what's there.
 *
 * Team-tab steps are `optional: true` so non-workspace-owners don't sit
 * through 8s waits on missing targets — the playlist gracefully advances
 * past them.
 */
export const analyticsPlaylist = [
    // ── Overview (visible regardless of tab) ────────────────────────────
    {
        selector: '.analytics-toolbar',
        title: 'Filter what you see',
        body: 'Pick a date range, a single platform, or export the whole view to CSV.',
    },
    {
        selector: '.analytics-tabs',
        title: 'Three views, one page',
        body: 'Planning shows what\'s scheduled and where the gaps are. Performance shows engagement metrics. Team shows who\'s creating what (workspace owners only).',
    },

    // ── Planning tab ────────────────────────────────────────────────────
    {
        selector: '[data-testid="analytics-kpi-grid"]',
        title: 'Planning at a glance',
        body: 'Scheduled posts, active days, draft backlog, and how many days of runway you have. Yellow = needs attention.',
        activateTab: 'planning',
    },
    {
        selector: '[data-testid="analytics-coverage"]',
        title: 'The next 30 days',
        body: 'A bar per day showing how many posts are scheduled. Gaps in the bar = empty days you should fill.',
        activateTab: 'planning',
        optional: true,
    },
    {
        selector: '[data-testid="analytics-platform-breakdown"]',
        title: 'Per-platform breakdown',
        body: 'How posts split across Facebook / Instagram / X / LinkedIn / Blog. Spot which platforms are starved.',
        activateTab: 'planning',
        optional: true,
    },
    {
        selector: '[data-testid="analytics-attention"]',
        title: 'Issues to fix',
        body: 'Failed publishes, stale drafts, missing media — anything that needs your attention before it bites.',
        activateTab: 'planning',
        optional: true,
    },

    // ── Performance tab ─────────────────────────────────────────────────
    {
        selector: '[data-testid="perf-sync-button"], #btn-perf-sync',
        title: 'Pull the latest engagement',
        body: 'Heist auto-syncs hourly. Hit Refresh to pull right now (rate-limited to once per 5 minutes).',
        activateTab: 'performance',
    },
    {
        selector: '[data-testid="perf-kpi-grid"]',
        title: 'Engagement KPIs',
        body: 'Impressions, engagement rate, likes, comments, shares — pulled live from your connected platforms.',
        activateTab: 'performance',
        optional: true,
    },
    {
        selector: '[data-testid="perf-trend-chart"]',
        title: 'Trend over time',
        body: 'See if engagement is climbing, flat, or dropping. Filter by platform up top to isolate one source.',
        activateTab: 'performance',
        optional: true,
    },
    {
        selector: '[data-testid="perf-posts-table"]',
        title: 'Top posts leaderboard',
        body: 'Your highest-performing posts in the range. Click any row to see the full breakdown — impressions, reach, clicks, link to the live post.',
        activateTab: 'performance',
        optional: true,
    },
    {
        selector: '[data-testid="perf-platform-breakdown"]',
        title: 'Engagement per platform',
        body: 'Which platform is paying off and which isn\'t. Compare side-by-side without juggling tabs.',
        activateTab: 'performance',
        optional: true,
    },
    {
        selector: '[data-testid="pdf-export-button"]',
        title: 'Client-ready PDF',
        body: 'One-click PDF report — KPIs, top 5 posts, by-platform table. Use it for client updates or a Friday-afternoon recap.',
        activateTab: 'performance',
        optional: true,
    },
    {
        selector: '[data-testid="engagement-chip-website"], [data-testid="engagement-chip-blog"]',
        title: 'Website + blog analytics',
        body: 'Add the Heist Pixel (or use URL monitoring) to track pageviews, referrers, and post cadence on your own site.',
        activateTab: 'performance',
        optional: true,
    },

    // ── Team tab (workspace owners + admins only) ───────────────────────
    {
        // The Team tab button is conditionally rendered — `optional`
        // ensures the tour skips past these steps cleanly for users who
        // don't have the role.
        selector: '[data-analytics-tab="team"]',
        title: 'Team activity (owners only)',
        body: 'Workspace owners + admins see a Team tab here — who created what, who reviewed it, and who\'s most active across brands.',
        activateTab: 'team',
        optional: true,
    },
];

const PAGE_PLAYLISTS = {
    home: homePlaylist,
    brain: brainPlaylist,
    // 'create' is a fallback if the dispatcher inside startCoachmarkTour()
    // somehow misses — it should never be hit in practice. The dispatcher
    // routes the bare 'create' name to one of the three sub-playlists
    // based on localStorage.activeCreateMode.
    create:        createSocialPlaylist,
    create_social: createSocialPlaylist,
    create_blog:   createBlogPlaylist,
    create_video:  createVideoPlaylist,
    library: libraryPlaylist,
    calendar: calendarPlaylist,
    campaigns: campaignsPlaylist,
    analytics: analyticsPlaylist,
    // 'connectors' aliases the existing MCP walkthrough so the per-page "?"
    // button on /#connectors fires the same 5-step playlist that the setup
    // wizard's heist-tour pivot uses.
    connectors: mcpFirstPlaylist,
};

/**
 * Public entry point. name = 'heist' | 'mcp' | 'settings' | one of the
 * page-scoped names in PAGE_PLAYLISTS. Optional opts.startAt overrides the
 * persisted resume step (used when the user explicitly restarts).
 */
export function startCoachmarkTour(name = 'heist', opts = {}) {
    // Phase 42 — when the page-header "?" button fires the bare 'create'
    // tour name, dispatch to the right sub-playlist based on which
    // workbench (Social / Blog / Video) the user is currently on.
    // The lookup is synchronous and harmless if localStorage is locked.
    if (name === 'create') {
        let mode = 'social';
        try { mode = localStorage.getItem('activeCreateMode') || 'social'; } catch (_) {}
        if (mode === 'blog')       name = 'create_blog';
        else if (mode === 'video') name = 'create_video';
        else                       name = 'create_social';
    }

    const playlist = name === 'mcp' ? mcpFirstPlaylist
        : name === 'settings' ? settingsPlaylist
        : (PAGE_PLAYLISTS[name] || heistFirstPlaylist);
    if (!playlist || !playlist.length) return;
    if (activeOverlay) return; // already running

    persistPlaylist(name);
    activePlaylist = playlist;
    activeName = name;
    activeStep = typeof opts.startAt === 'number' ? opts.startAt : (opts.resume ? readStep() : 0);
    if (activeStep >= playlist.length) activeStep = 0;

    mountOverlay();
    showStep(activeStep);
}

/**
 * Resume the tour at the last persisted step. Used by the orchestrator on
 * boot when `localStorage` shows tour was in progress and the user is
 * authenticated but `user.tour_completed` is still false.
 */
export function resumeCoachmarkTour() {
    const name = readPlaylistName();
    startCoachmarkTour(name, { resume: true });
}

function mountOverlay() {
    activeOverlay = document.createElement('div');
    activeOverlay.className = 'coachmark-overlay';
    // 4 mask divs (top/right/bottom/left of the target) + ring + tooltip
    activeOverlay.innerHTML = `
      <div class="coachmark-mask coachmark-mask--top"></div>
      <div class="coachmark-mask coachmark-mask--right"></div>
      <div class="coachmark-mask coachmark-mask--bottom"></div>
      <div class="coachmark-mask coachmark-mask--left"></div>
      <div class="coachmark-ring"></div>
      <div class="coachmark-tip" role="dialog" aria-live="polite">
        <div class="coachmark-tip__progress"></div>
        <h3 class="coachmark-tip__title"></h3>
        <p class="coachmark-tip__body"></p>
        <div class="coachmark-tip__actions">
          <button class="coachmark-tip__skip" data-action="skip">Skip tour</button>
          <button class="coachmark-tip__next" data-action="next">Next <span class="i-svg-inline">${arrowRight()}</span></button>
        </div>
      </div>
    `;
    document.body.appendChild(activeOverlay);

    activeOverlay.addEventListener('click', (e) => {
        const action = e.target.closest('[data-action]')?.dataset?.action;
        if (action === 'skip') return finishTour({ skipped: true });
        if (action === 'next') return advance();
    });

    onKeyDown = (e) => {
        if (e.key === 'Escape') finishTour({ skipped: true });
    };
    document.addEventListener('keydown', onKeyDown);

    onResize = () => {
        cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(() => positionForCurrentStep());
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
}

function showStep(idx) {
    if (!activeOverlay || !activePlaylist) return;
    activeStep = idx;
    persistStep(idx);
    const step = activePlaylist[idx];
    if (!step) return finishTour({ completed: true });

    // Detach the previous step's onTargetClick listener BEFORE running any
    // clickFirst or activateTab side effects. Without this, the click that
    // clickFirst fires on, say, the Brand-tab button bubbles up and the
    // old listener (which was still watching for clicks inside the
    // previous step's spotlight area) calls advance() — skipping the
    // current step entirely. Verified on the brain tour where the brand
    // tab is a child of .brain-tabs (the prior step's target).
    if (onTargetClick) {
        document.removeEventListener('click', onTargetClick, true);
        onTargetClick = null;
    }
    // Same race risk for the wait observer — clear any in-flight observer
    // from the previous step so its setInterval doesn't fire renderNow()
    // for the wrong step.
    clearWaitObserver();

    // If the step is anchored to a settings tab, ask the settings page to
    // activate that tab BEFORE the spotlight measures rects — otherwise
    // the user reads about Schedule while still looking at Integrations.
    // Settings page listens via the heist:tour-activate-section custom event
    // and re-renders synchronously; the wait-for-rect loop handles the brief
    // DOM swap that follows.
    if (step.settingsSectionId) {
        try {
            window.dispatchEvent(new CustomEvent('heist:tour-activate-section', {
                detail: { sectionId: step.settingsSectionId },
            }));
        } catch (_) {}
    }

    // Phase 4 (analytics tour): if a step targets an analytics-page tab,
    // click the tab button before measuring rects so the user is looking
    // at the right tab content. Same pattern as settingsSectionId but
    // page-specific. The wait-for-rect loop below handles the re-render.
    if (step.activateTab) {
        try {
            const tabBtn = document.querySelector(`[data-analytics-tab="${step.activateTab}"]`);
            if (tabBtn && !tabBtn.classList.contains('active')) {
                tabBtn.click();
            }
        } catch (_) {}
    }

    // Phase 4b (cross-page tab/mode walking): generic version of the
    // analytics activateTab pattern. Any step can declare
    //   clickFirst: '<css-selector>'
    // and the tour will click that element before measuring rects, but
    // only if the element exists AND is not already in the .active or
    // .is-active state. Used to walk Brain tabs, Calendar view modes,
    // Create modes, etc., without one orchestration field per page.
    if (step.clickFirst) {
        try {
            const el = document.querySelector(step.clickFirst);
            if (el && !el.classList.contains('active') && !el.classList.contains('is-active')) {
                el.click();
            }
        } catch (_) {}
    }

    const renderNow = () => {
        // Make sure the target is actually visible before we anchor to it.
        // Without this, a step whose target sits below the fold (or scrolled
        // out by an earlier deep-link) ends up with the spotlight ring above
        // the viewport and the masks freeze the screen with nothing useful
        // visible. scrollIntoView with block:'center' brings it into the
        // middle of the viewport so the tooltip has room on either side.
        const targetEl = document.querySelector(step.selector);
        if (targetEl && typeof targetEl.scrollIntoView === 'function') {
            try { targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
        }
        // The smooth scroll may take ~300ms to settle; reposition once
        // immediately for the no-scroll-needed case, then again after the
        // animation so the spotlight tracks the final position.
        positionForCurrentStep();
        setTimeout(() => positionForCurrentStep(), 350);

        // Tooltip content
        const tip = activeOverlay.querySelector('.coachmark-tip');
        tip.querySelector('.coachmark-tip__progress').textContent = `Step ${idx + 1} of ${activePlaylist.length}`;
        tip.querySelector('.coachmark-tip__title').textContent = step.title;
        // Body supports a small set of inline HTML tags (<strong>, <em>,
        // <code>, <br>, <a>) so step copy can emphasise key terms. All
        // bodies are source-defined constants in this file — never user
        // input — so direct innerHTML is safe here. Sanitiser is a thin
        // allowlist to keep the trust boundary explicit even if a future
        // step body gets sourced dynamically.
        tip.querySelector('.coachmark-tip__body').innerHTML = sanitizeTourBody(step.body);
        // Drive the gradient progress bar via a CSS variable. (idx+1)/length
        // gives the % of the way through. The CSS reads --coachmark-progress
        // and renders the bar fill width accordingly.
        const pct = Math.round(((idx + 1) / activePlaylist.length) * 100);
        tip.style.setProperty('--coachmark-progress', pct + '%');
        const nextBtn = tip.querySelector('.coachmark-tip__next');
        const isLast = idx === activePlaylist.length - 1;
        // Pivot steps get a labelled CTA so the user understands the click
        // will navigate them, not just close the tooltip. innerHTML so the
        // inline SVG arrow renders as markup instead of literal text.
        const arrowMarkup = `<span class="i-svg-inline">${arrowRight()}</span>`;
        if (isLast && step.pivotTo === 'mcp') nextBtn.innerHTML = `Show me MCP ${arrowMarkup}`;
        else nextBtn.innerHTML = isLast ? 'Done' : `Next ${arrowMarkup}`;

        // Click on the highlighted element advances too. Capture in bubble
        // phase but don't preventDefault — let the link/button do its job.
        // `noClickAdvance` opts a step out: used for nav-away rows (Connectors,
        // Agent Inbox) where clicking navigates the user off the page and we
        // don't want the tour to chase them by advancing in the background.
        if (onTargetClick) document.removeEventListener('click', onTargetClick, true);
        if (!step.noClickAdvance) {
            onTargetClick = (e) => {
                const target = document.querySelector(step.selector);
                if (!target) return;
                if (target.contains(e.target)) {
                    // Tiny delay so the user sees the highlighted element's own click feedback
                    setTimeout(() => advance(), 120);
                }
            };
            document.addEventListener('click', onTargetClick, true);
        } else {
            onTargetClick = null;
        }
    };

    // Always wait for the actual target to exist AND have a non-zero rect
    // before rendering. The selector may resolve to an element that has
    // 0×0 bounds during early sidebar/page mount; we'd otherwise place the
    // ring + tip at (0,0). `waitForSelector` is the explicit guard for
    // page-content steps; for sidebar steps we use the step's selector
    // itself.
    const waitTarget = step.waitForSelector || step.selector;
    const targetReady = () => {
        const el = document.querySelector(waitTarget);
        if (!el) return false;
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
    };
    // `optional: true` — skip immediately when the target isn't in the DOM.
    // Used for tier/role-gated rows (Connectors, Agent Inbox, Approvals,
    // Activity) so non-Pro / non-admin users don't watch an 8-second hidden-
    // tooltip wait between every visible step.
    //
    // EXCEPTION: when a step has `clickFirst`, we DO wait the full 8s —
    // the click is supposed to make the target appear, but the re-render
    // takes a moment. Skipping immediately would defeat the purpose of
    // the tab/mode switch.
    if (step.optional && !step.clickFirst && !targetReady()) {
        return advance();
    }
    if (!targetReady()) {
        const tip = activeOverlay.querySelector('.coachmark-tip');
        const ring = activeOverlay.querySelector('.coachmark-ring');
        tip.style.opacity = '0';
        ring.style.opacity = '0';
        clearWaitObserver();
        const start = Date.now();
        pendingWaitObserver = setInterval(() => {
            if (targetReady()) {
                tip.style.opacity = '';
                ring.style.opacity = '';
                clearWaitObserver();
                renderNow();
            } else if (Date.now() - start > 8000) {
                // Give up — skip the step rather than show it at (0,0)
                clearWaitObserver();
                advance();
            }
        }, 150);
    } else {
        renderNow();
    }
}

function clearWaitObserver() {
    if (pendingWaitObserver) {
        clearInterval(pendingWaitObserver);
        pendingWaitObserver = null;
    }
}

function positionForCurrentStep() {
    if (!activeOverlay || !activePlaylist) return;
    const step = activePlaylist[activeStep];
    if (!step) return;
    const target = document.querySelector(step.selector);
    if (!target) return;
    const rect = target.getBoundingClientRect();
    // Bail if the target is unrendered (zero rect) — without this the
    // masks + tooltip get pinned to (0,0) and the spotlight ends up in
    // the top-left corner. The waitForRect loop in showStep will retry.
    if (rect.width === 0 || rect.height === 0) return;

    const top = rect.top - RING_PADDING;
    const left = rect.left - RING_PADDING;
    const width = rect.width + RING_PADDING * 2;
    const height = rect.height + RING_PADDING * 2;
    const right = left + width;
    const bottom = top + height;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const masks = activeOverlay.querySelectorAll('.coachmark-mask');
    Object.assign(masks[0].style, { top: '0px', left: '0px', width: vw + 'px', height: Math.max(0, top) + 'px' });
    Object.assign(masks[1].style, { top: top + 'px', left: right + 'px', width: Math.max(0, vw - right) + 'px', height: Math.max(0, height) + 'px' });
    Object.assign(masks[2].style, { top: bottom + 'px', left: '0px', width: vw + 'px', height: Math.max(0, vh - bottom) + 'px' });
    Object.assign(masks[3].style, { top: top + 'px', left: '0px', width: Math.max(0, left) + 'px', height: Math.max(0, height) + 'px' });

    const ring = activeOverlay.querySelector('.coachmark-ring');
    Object.assign(ring.style, { top: top + 'px', left: left + 'px', width: width + 'px', height: height + 'px' });

    const tip = activeOverlay.querySelector('.coachmark-tip');
    // Default placement: right of target if there's room, else below.
    const tipMargin = 14;
    const tipMaxWidth = 320;
    let tipLeft = right + tipMargin;
    let tipTop = top;
    if (tipLeft + tipMaxWidth > vw - 16) {
        // not enough room to the right — place below
        tipLeft = Math.max(16, Math.min(left, vw - tipMaxWidth - 16));
        tipTop = bottom + tipMargin;
    }
    if (tipTop + 200 > vh) {
        // not enough room below — place above
        tipTop = Math.max(16, top - 220);
    }
    Object.assign(tip.style, { top: tipTop + 'px', left: tipLeft + 'px' });
}

function advance() {
    if (!activePlaylist) return;
    const next = activeStep + 1;
    if (next >= activePlaylist.length) {
        // Capture pivot from the just-finished step BEFORE cleanup wipes state.
        const lastStep = activePlaylist[activeStep];
        return finishTour({ completed: true, pivotTo: lastStep && lastStep.pivotTo });
    }
    showStep(next);
}

function finishTour(opts) {
    const name = activeName;
    const pivotTo = opts && opts.pivotTo;
    cleanupOverlay();
    clearStorage();
    // Only mark the server-side tour_completed flag for the first-run tours.
    // Re-runnable tours (e.g. 'settings') stay independent of that flag so
    // they can be triggered repeatedly without flipping onboarding state.
    if (name === 'heist' || name === 'mcp') {
        client.completeTour(opts && opts.skipped ? { skipped: true } : {}).catch(() => {});
        if (window.__auth__) window.__auth__.tour_completed = true;
    }
    // Pivot: chain into the next playlist on a different page. Used by the
    // last step of `heist` to roll directly into the MCP walkthrough so a
    // user who finishes the in-app tour gets nudged toward Claude/ChatGPT
    // setup instead of being told "we'll show you that later."
    if (pivotTo === 'mcp' && name !== 'mcp') {
        // Navigate to the connectors page; the page itself sets up the
        // mcpFirstPlaylist's target selectors when it mounts. A short delay
        // lets the route change settle before the new tour starts.
        try { window.location.hash = '#connectors'; } catch (_) {}
        setTimeout(() => {
            if (!isCoachmarkActive()) startCoachmarkTour('mcp');
        }, 600);
    }
}

function cleanupOverlay() {
    clearWaitObserver();
    if (onTargetClick) {
        document.removeEventListener('click', onTargetClick, true);
        onTargetClick = null;
    }
    if (onKeyDown) {
        document.removeEventListener('keydown', onKeyDown);
        onKeyDown = null;
    }
    if (onResize) {
        window.removeEventListener('resize', onResize);
        window.removeEventListener('scroll', onResize, true);
        onResize = null;
    }
    if (activeOverlay && activeOverlay.parentNode) {
        activeOverlay.parentNode.removeChild(activeOverlay);
    }
    activeOverlay = null;
    activePlaylist = null;
    activeStep = -1;
    activeName = null;
}

export function isCoachmarkActive() {
    return !!activeOverlay;
}

export function abortCoachmarkTour() {
    if (activeOverlay) cleanupOverlay();
}
